//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { InvisibleButton } from '@components/atoms/button/Button';
import {
  BodySecondaryText,
  LinkPrimaryText,
} from '@components/atoms/text/Text';
import { MediumTooltip } from '@components/molecules/tooltip/Tooltip';
import DateString from '@models/DateString';
import { getUserTimeZone } from '@redux/selectors/getUserTimeZone';
import { UIA_RIGHTS_FLOW_TRACKING_IDS } from '@uia/rights/constants/uiaRightsConstants';
import { isDefined } from '@utils/filterPredicates';

interface ExpiryInfoProps {
  expirationTime?: Date;
  isDisabled: boolean;
  onClick: () => void;
}

export const ExpiryInfo = ({
  expirationTime,
  isDisabled,
  onClick,
}: ExpiryInfoProps) => {
  const timezone = useSelector(getUserTimeZone);

  return (
    <Container>
      {isDefined(expirationTime) && (
        <StyledText>
          {`Rights will expire on ${DateString.format(DateString.fromTimestamp(expirationTime, timezone), 'MM/DD/YY')}.`}
        </StyledText>
      )}
      <MediumTooltip
        content={
          <TooltipContainer>
            An expiry date cannot be set for content already distributed to the
            Visual Syndication Network.
          </TooltipContainer>
        }
        isDisabled={!isDisabled}
        placement='top'
        target={
          <UpdateExpiryDate
            isDisabled={isDisabled}
            onClick={onClick}
            trackingId={UIA_RIGHTS_FLOW_TRACKING_IDS.UPSERT_EXPIRY_DATE}
          >
            <StyledLinkPrimaryText $isDisabled={isDisabled}>
              {isDefined(expirationTime) ? 'Edit' : 'Add'} expiry date
            </StyledLinkPrimaryText>
          </UpdateExpiryDate>
        }
      />
    </Container>
  );
};

const UpdateExpiryDate = styled(InvisibleButton)``;

const StyledText = styled(BodySecondaryText)`
  text-align: center;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledLinkPrimaryText = styled(LinkPrimaryText)<{
  $isDisabled?: boolean;
}>`
  ${({ $isDisabled, theme }) => `
    color: ${$isDisabled ? theme.colors.aliases.disabled : theme.colors.aliases.primary};
  `}
`;

const TooltipContainer = styled.div(
  ({ theme }) => `
  width: ${theme.smallMeasure};
  text-align: center;
`,
);
